import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderItemType } from '@global/utils/orderUtils';
import { CommonCheckbox } from '@global/components/CommonCheckbox';
import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import ExternalLink from '@assets/images/externalLink.svg';
import './styles.scss';

interface IProps {
  orderItems: OrderItemType[];
  handleClose: () => void;
  handleAccept: () => void;
}

export const LicenseModal: React.FC<IProps> = ({
  orderItems,
  handleClose,
  handleAccept,
}) => {
  const [checkedAgreements, setCheckedAgreements] = useState([]);
  const { t } = useTranslation('', {
    keyPrefix: 'checkout.review.license',
  });

  const licensedItems = useMemo<string[][]>(() => {
    const items = orderItems.reduce((acc, item) => {
      if (item.licenseAgreementInfo) {
        const { wplaiName, wplaiUrl } = item.licenseAgreementInfo;
        acc[wplaiName] = wplaiUrl;
      }
      return acc;
    }, {});

    return Object.entries(items);
  }, [orderItems]);

  const disabledCta = checkedAgreements.length !== licensedItems.length;

  const toggleCheckbox = (name) => () => {
    if (checkedAgreements.includes(name)) {
      const filteredList = checkedAgreements.filter((n) => n !== name);
      setCheckedAgreements(filteredList);
    } else {
      setCheckedAgreements([...checkedAgreements, name]);
    }
  };

  const onAccept = () => {
    handleClose();
    handleAccept();
  };

  return (
    <>
      <h3>{t('title')}</h3>
      <p>{t('description')}</p>
      {licensedItems.map(([name, url]) => (
        <div key={name} className="license-modal__item">
          <div className="license-modal__checkbox-wrapper">
            <CommonCheckbox
              toggle={toggleCheckbox(name)}
              checked={checkedAgreements.includes(name)}
            />
            <p className="license-modal__item-name">{name}</p>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="license-modal__link"
          >
            <p>{t('link')}</p>
            <img src={ExternalLink} alt="License external link" />
          </a>
        </div>
      ))}
      <div className="license-modal__buttons">
        <ButtonCTA variant="primary" onClick={onAccept} disabled={disabledCta}>
          {t('accept')}
        </ButtonCTA>
        <ButtonCTA variant="secondary" onClick={handleClose}>
          {t('cancel')}
        </ButtonCTA>
      </div>
    </>
  );
};
